import { Link } from 'react-router-dom';
import { Sparkles, Zap, ShoppingBag, TrendingUp } from 'lucide-react';

export default function Landing() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-brand-50 to-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Sparkles className="w-8 h-8 text-brand-600" />
              <span className="ml-2 text-xl font-bold text-surface-900">Virtual Try-On</span>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/pricing" className="text-surface-700 hover:text-surface-900">
                Pricing
              </Link>
              <Link to="/login" className="text-surface-700 hover:text-surface-900">
                Login
              </Link>
              <Link
                to="/signup"
                className="bg-brand-600 text-white px-4 py-2 rounded-xl hover:bg-brand-700"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-surface-900 mb-6">
            AI-Powered Virtual Try-On
            <br />
            <span className="text-brand-600">For Your WooCommerce Store</span>
          </h1>
          <p className="text-xl text-surface-600 mb-8 max-w-3xl mx-auto">
            Let your customers visualize products on different body types instantly.
            Boost conversions by 40% with realistic AI-generated try-ons.
          </p>
          <div className="flex justify-center space-x-4">
            <Link
              to="/signup"
              className="bg-brand-600 text-white px-8 py-3 rounded-xl text-lg font-medium hover:bg-brand-700 shadow-card"
            >
              Start Free Trial
            </Link>
            <Link
              to="/pricing"
              className="bg-white text-brand-600 px-8 py-3 rounded-xl text-lg font-medium border-2 border-brand-600 hover:bg-brand-50"
            >
              View Pricing
            </Link>
          </div>
          <p className="mt-4 text-surface-500">No credit card required • 7-day free trial</p>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-8">
          <FeatureCard
            icon={<Zap className="w-12 h-12 text-brand-600" />}
            title="Lightning Fast"
            description="Cached results load in under 500ms. First-time generations in 15-20 seconds."
          />
          <FeatureCard
            icon={<ShoppingBag className="w-12 h-12 text-brand-600" />}
            title="Easy Integration"
            description="Simple WordPress plugin. No coding required. Works with any WooCommerce theme."
          />
          <FeatureCard
            icon={<TrendingUp className="w-12 h-12 text-brand-600" />}
            title="Boost Conversions"
            description="Studies show virtual try-on increases purchase confidence by 40%."
          />
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-surface-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-4 gap-8">
            <Step number="1" title="Sign Up" description="Create your account in 2 minutes" />
            <Step number="2" title="Install Plugin" description="Install our WordPress plugin" />
            <Step number="3" title="Connect Store" description="Link your WordPress site" />
            <Step number="4" title="Go Live" description="Customers can try on products!" />
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-brand-600 rounded-2xl p-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to Transform Your Store?
          </h2>
          <p className="text-xl text-brand-100 mb-8">
            Join hundreds of stores using AI-powered virtual try-ons
          </p>
          <Link
            to="/signup"
            className="inline-block bg-white text-brand-600 px-8 py-3 rounded-xl text-lg font-medium hover:bg-brand-50 shadow-card"
          >
            Start Your Free Trial
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-surface-900 text-surface-300 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p>&copy; 2025 Virtual Try-On SaaS. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-card hover:shadow-panel transition-shadow">
      <div className="mb-4">{icon}</div>
      <h3 className="text-xl font-bold mb-2">{title}</h3>
      <p className="text-surface-600">{description}</p>
    </div>
  );
}

function Step({ number, title, description }) {
  return (
    <div className="text-center">
      <div className="bg-brand-600 text-white w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
        {number}
      </div>
      <h3 className="text-lg font-bold mb-2">{title}</h3>
      <p className="text-surface-600">{description}</p>
    </div>
  );
}
